import { useMemo } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useActiveOrgSlug } from '@reflex/ui/client/context';
import { withOrgSlug } from '@reflex/shared/routes';
import { NameDialog } from '@reflex/ui/components/shared/NameDialog';
import { useCreateFlowReplayFromRun } from './useFlowReplays.js';

interface SaveAsFlowReplayDialogProps {
  flowRunId: string;
  defaultName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog used by `FlowRunPage` to capture a completed run as a saved
 * flow replay. Wraps `useCreateFlowReplayFromRun`, mirrors the
 * existing `SaveAsReplayDialog` pattern from `plugin-replay`, and
 * navigates to the new flow replay's detail page on success.
 */
export function SaveAsFlowReplayDialog({
  flowRunId,
  defaultName,
  open,
  onOpenChange,
}: SaveAsFlowReplayDialogProps) {
  const createReplay = useCreateFlowReplayFromRun();
  const navigate = useNavigate();
  const slug = useActiveOrgSlug();
  const values = useMemo(() => ({ name: defaultName, description: '' }), [defaultName]);

  return (
    <NameDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Save as automation replay"
      description="Captures this run's graph timeline plus per-agent replays so you can play it back later or embed it in marketing pages."
      nameLabel="Replay name"
      namePlaceholder="My automation run"
      descriptionPlaceholder="What was this run about?"
      submitLabel="Save replay"
      isPending={createReplay.isPending}
      values={values}
      onSubmit={(data) => {
        createReplay.mutate(
          {
            flowRunId,
            name: data.name,
            description: data.description || undefined,
          },
          {
            onSuccess: (replay) => {
              toast.success('Automation replay saved');
              onOpenChange(false);
              navigate(withOrgSlug(slug, `/automation-replays/${replay.id}`));
            },
            onError: (err: Error) =>
              toast.error('Failed to save automation replay', { description: err.message }),
          },
        );
      }}
    />
  );
}
